import React, {
  useContext,
  useCallback,
  useState,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import { createDialogContext, DialogProvider } from "../../../Common/Dialog";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  Box,
  ModalHeader,
  Divider,
  ModalBody,
  ModalFooter,
  HStack,
  Button,
  Text,
  Badge,
  useToast,
  FormControl,
  Table,
  Thead,
  Tbody,
  Select as ChakraSelect,
  Tr,
  Th,
  Td,
  VStack,
  Icon,
  Heading,
  InputGroup,
  Input,
} from "@chakra-ui/react";
import { FaFileCsv } from "react-icons/fa";
import actions from "src/actions";
import ReactS3Uploader, { S3Response } from "react-s3-uploader";
import GenresSection from "../../EventDetail/Genres/GenresSection";
import { KYDGenre } from "@common/interfaces/KYDEvent";
import { KYDGenresList } from "src/Manager/Genres";
import { MultiValue, Select } from "chakra-react-select";
import { useForm } from "react-hook-form";

export const ImportContactsDialogContext = createDialogContext();

const POSSIBLE_FIELDS = [
  { label: "First Name", value: "first_name" },
  { label: "Last Name", value: "last_name" },
  { label: "Full Name", value: "full_name" },
  { label: "Spend", value: "spend" },
  { label: "Ticket Quantity", value: "tickets" },
  { label: "Email", value: "email" },
  { label: "Phone", value: "phone" },
  { label: "Birthday (MM-DD-YYYY)", value: "dob" },
  { label: "Gender", value: "gender" },
  { label: "Tags (comma separated)", value: "tags" },
];

interface ImportContactsDialogProps {
  title?: string;
  isOpen: boolean;
  close: () => void;
  onSubmit?: (data: { data: any; response: any }) => void;
}

const ImportContactsDialog = ({
  title = "Import Contacts",
  isOpen,
  onSubmit = () => {},
  close,
}: ImportContactsDialogProps) => {
  const toast = useToast();
  const { register, handleSubmit, reset, setValue, watch } = useForm({
    defaultValues: {
      name: "",
      genres: [],
      mapping: [],
      file_key: "",
    },
  });

  const setMapping = (mapping: string[]) => {
    setValue("mapping", mapping);
  };

  const mapping = watch("mapping");
  const genres = watch("genres");
  const name = watch("name");

  const [file, setFile] = useState<File | null>(null);
  const [parsedHeaders, setParsedHeaders] = useState<string[]>([]);
  const [parsedRows, setParsedRows] = useState<string[][]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onDrop = useCallback(
    (accepted: File[]) => {
      if (!accepted.length) return;
      const f = accepted[0];
      setFile(f);

      Papa.parse<string[]>(f, {
        preview: 11,
        skipEmptyLines: true,
        complete: (results) => {
          const data = results.data;
          if (data.length < 2) {
            toast({ status: "error", title: "CSV must have at least 2 rows" });
            setFile(null);
            return;
          }
          setParsedHeaders(data[0]);
          setParsedRows(data.slice(1));
          setMapping(data[0].map(() => ""));
        },
        error: (err) =>
          toast({
            status: "error",
            title: "Parse error",
            description: err.message,
          }),
      });
    },
    [toast]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: { "text/csv": [".csv"] },
  });

  const handleImportClick = () => {
    if (!file || !inputRef.current) return;
    setIsUploading(true);

    const dt = new DataTransfer();
    dt.items.add(file);
    inputRef.current.files = dt.files as any;

    inputRef.current.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const isFormValid = useMemo(() => {
    return (
      file &&
      (mapping.includes("first_name") || mapping.includes("full_name")) &&
      mapping.includes("email") &&
      !isUploading &&
      genres.length > 0 &&
      name
    );
  }, [file, mapping, isUploading, genres, name]);

  const _onSubmit = async (data: {
    name: string;
    genres: { value: string; label: string }[];
    mapping: string[];
    file_key: string;
  }) => {
    toast({ status: "success", title: "Import started" });
    close();

    if (!data.file_key) {
      toast({
        status: "error",
        title: "Upload failed",
        description: "No fileKey returned.",
      });
      return;
    }

    const response = await actions.ADMIN.contactImportCsv(data.file_key, {
      mapping: data.mapping,
      genres: data.genres.map((g) => g.value),
      name: data.name,
    });

    reset();

    onSubmit({ data, response });
  };

  const onUploadFinish = (res: S3Response) => {
    setIsUploading(false);

    if (res.fileKey) {
      console.log({
        res,
      });
      setValue("file_key", res.fileKey);
      handleSubmit(_onSubmit)();
    } else {
      toast({
        status: "error",
        title: "Upload failed",
        description: "No fileKey returned.",
      });
    }
  };

  const onUploadError = (err: Error) => {
    setIsUploading(false);
    toast({
      status: "error",
      title: "Upload to S3 failed",
      description: err.message,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={close} size="3xl" isCentered>
      <ModalOverlay />
      <ModalContent bg="white" borderRadius="lg" p={0}>
        <Box p={6}>
          <ModalHeader p={0} mb={4} fontSize="2xl">
            <HStack justify="space-between">
              <Text fontWeight="400" fontSize="24px">
                {title}
              </Text>
              <HStack spacing={3}>
                <Button
                  variant="outline"
                  rounded="full"
                  fontSize="13px"
                  onClick={() => {
                    setFile(null);
                    setParsedHeaders([]);
                    setParsedRows([]);
                    setMapping([]);
                    close();
                  }}
                  isDisabled={isUploading}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="green"
                  rounded="full"
                  fontSize="13px"
                  onClick={() => {
                    handleImportClick();
                  }}
                  isDisabled={!isFormValid}
                  isLoading={isUploading}
                  loadingText="Uploading..."
                >
                  Import
                </Button>
              </HStack>
            </HStack>
          </ModalHeader>

          <Divider borderBottom="1px solid rgba(0,0,0,0.20)" mb={4} />

          {/* <GenresSection
            data={{ genres }}
            onSave={async (genresSelected: { name: string; id: string }[]) => {
              console.log(genresSelected);
              setGenres(genresSelected);
            }}
            isLoading={false}
          /> */}
          <VStack w="full" spacing={4} my={6} align="flex-start">
            <VStack align="flex-start" w="full" spacing={1}>
              <VStack w="full" align="flex-start">
                <Heading size="sm">Name</Heading>
                <Input placeholder="Name of the import" {...register("name")} />
              </VStack>
              <VStack w="full" align="flex-start">
                <Heading size="sm">Genres</Heading>
                <Text fontSize="sm" color="gray.500">
                  Select the genres that will be associated with the imported
                  fans.
                </Text>
                <Select
                  isMulti
                  isDisabled={false}
                  closeMenuOnSelect={false}
                  // @ts-ignore
                  value={genres}
                  // @ts-ignore
                  onChange={(values) => setValue("genres", values)}
                  chakraStyles={{
                    container: () => ({
                      position: "relative",
                      width: "100%",
                    }),
                  }}
                  //@ts-ignore
                  options={KYDGenresList.map((g) => ({
                    label: g.label,
                    value: g.name,
                  }))}
                />
              </VStack>
            </VStack>
          </VStack>

          <Badge
            bg="#718096"
            color="white"
            rounded="full"
            mb={2}
            py={1}
            px={3}
            fontSize="12px"
            fontWeight="500"
          >
            Import of large CSV's may take up to 3 hrs
          </Badge>
          <VStack align="flex-start" spacing={2} mb={4}>
            <Text fontSize="sm">
              The CSV must include at least the first names of all fans.
            </Text>
            <Text fontSize="sm">
              Please assign the column headers to the rows. Unassigned columns
              will be ignored.
            </Text>
            <Text fontSize="sm" color="#CC2929" fontWeight="600">
              First name and email are required*
            </Text>
          </VStack>

          <ModalBody p={0} overflow="visible">
            <ReactS3Uploader
              style={{ display: "none" }}
              //@ts-ignore
              inputRef={inputRef}
              getSignedUrl={(f, cb) => actions.ADMIN.createUploadUrl(f, cb)}
              onError={(msg) => onUploadError(new Error(msg))}
              onFinish={onUploadFinish}
              accept="text/csv"
              uploadRequestHeaders={(f) => ({
                "Content-Type": f.type,
                "x-amz-acl": "private",
              })}
            />

            {!parsedHeaders.length ? (
              <Box
                {...getRootProps()}
                bg="gray.100"
                borderColor={isDragActive ? "green.500" : "gray.300"}
                borderRadius="md"
                py={12}
                textAlign="center"
                cursor="pointer"
              >
                <input {...getInputProps()} />
                <Icon as={FaFileCsv} boxSize={8} color="gray.400" />
                <Text fontWeight="600" mt={6}>
                  UPLOAD FILE
                </Text>
                <Text>Drop CSV file here or click to select</Text>
              </Box>
            ) : (
              <VStack align="stretch" spacing={2} mt={4}>
                <Box
                  overflowX="auto"
                  overflowY="auto"
                  maxH="500px"
                  border="1px solid #E2E8F0"
                  borderRadius="md"
                >
                  <Table
                    variant="simple"
                    size="md"
                    whiteSpace="nowrap"
                    minW="max-content"
                  >
                    <Thead>
                      <Tr>
                        {parsedHeaders.map((_, idx) => {
                          const others = mapping.filter((_, i) => i !== idx);
                          const available = POSSIBLE_FIELDS.filter(
                            (f) =>
                              f.value === mapping[idx] ||
                              !others.includes(f.value)
                          );

                          return (
                            <Th key={idx} px={2} py={1}>
                              <FormControl>
                                <ChakraSelect
                                  size="xs"
                                  value={mapping[idx]}
                                  onChange={(e) => {
                                    const m = [...mapping];
                                    m[idx] = e.target.value;
                                    setMapping(m);
                                  }}
                                >
                                  <option value="">None</option>
                                  {available.map((f) => (
                                    <option key={f.value} value={f.value}>
                                      {f.label}
                                    </option>
                                  ))}
                                </ChakraSelect>
                              </FormControl>
                            </Th>
                          );
                        })}
                      </Tr>
                      <Tr>
                        {parsedHeaders.map((col, idx) => (
                          <Th key={idx} px={2} py={2} fontSize="sm">
                            {col}
                          </Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {parsedRows.map((row, ri) => (
                        <Tr key={ri}>
                          {row.map((cell, ci) => (
                            <Td key={ci} px={2} py={3} fontSize="14px">
                              {cell}
                            </Td>
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </Box>
        <ModalFooter />
      </ModalContent>
    </Modal>
  );
};

// /* ---------- HOC wrapper ---------- */
// export const withImportContactsDialog =
//   <P extends object>(Component: React.ComponentType<P>) =>
//   (props: P) => (
//     <DialogProvider context={ImportContactsDialogContext}>
//       <Component {...props} />
//       <ImportContactsDialog />
//     </DialogProvider>
//   );

export default ImportContactsDialog;
